import { checkParentTarget, conditions, isTouchScreen } from "helpers";
import { MouseEvent, useEffect, useState } from "react";
import { handleFullScreen, handlePlayPause, handleSubtitle, handleVideoElementSizeInfo } from "redux-share/actions";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import { ConditionName, zIndex } from "interfaces";
import useActiveInActive from "helpers/hooks/useActiveInActive";
import { setShowMiniPlayer } from "redux-share/slices/controllerSlice";
import PlayerCore from "ui/src/components/player-core/playerCore";
import { shallowEqual } from "react-redux";
import Image from "next/image";
import dynamic from "next/dynamic";
import VideoAreaShadow from "../../../../player-components/videoAreaShadow.tsx/videoAreaShadow";

const TouchVolumeBrightness = dynamic(() => import("ui/src/components/player-components/touchVolumeBrightness/touchVolumeBrightness"));
const BackwardForward = dynamic(() => import("ui/src/components/player-components/backwardForward/backwardForward"));
const AutoPlay = dynamic(() => import("ui/src/components/player-components/autoPlay/autoPlay"));
const Gif = dynamic(() => import("ui/src/components/player-components/gif/gif"));
const CloseIcon = dynamic(() => import("ui/src/components/icon-components/closeIcon"));
const Header = dynamic(() => import("ui/src/components/mainComponent/header/header"));
const PortraitHeader = dynamic(() => import("ui/src/components/mainComponent/header/portraitHeader"));
const Main = dynamic(() => import("ui/src/components/mainComponent/main/main"));
const Footer = dynamic(() => import("ui/src/components/mainComponent/footer/footer"));
const ContinuousPlayback = dynamic(() => import("../../../../../playerComponents/continuousPlayback/ContinuousPlayback"));

const VideoArea = () => {
    const [isRightDoubleTouched, setIsRightDoubleTouched] = useState<boolean>(false);
    const [isLeftDoubleTouched, setIsLeftDoubleTouched] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { gif, details } = useAppSelector(
        ({ videoData: { gif, details, recommendationList, params, videoElementSizeInfo } }) => ({
            gif,
            details,
            recommendationList,
            params,
            videoElementSizeInfo
        }),
        shallowEqual
    );
    const { showMiniPlayer, firstPlay } = useAppSelector(
        ({ controller: { showMiniPlayer, firstPlay, lock, isEnded, isAutoPlay } }) => ({ showMiniPlayer, firstPlay, lock, isEnded, isAutoPlay }),
        shallowEqual
    );
    useAppSelector(({ popovers: { autoPlayPopover } }) => ({ autoPlayPopover }), shallowEqual);
    useAppSelector(({ user: { videoSettings, showContinuousePlayback } }) => ({ videoSettings, showContinuousePlayback }), shallowEqual);

    const handleDoubleClick = (event: any) => {
        if (
            !conditions(ConditionName.isTouchScreen) &&
            !checkParentTarget(event, "emojiButtonLeft") &&
            !checkParentTarget(event, "emojiButtonRight") &&
            !checkParentTarget(event, "menuIconLeft") &&
            !checkParentTarget(event, "menuIconRight") &&
            event.target.nodeName !== "INPUT"
        )
            handleFullScreen();
    };

    // --- Managing the active and inActive of elements in desktop and mobile devices --- //
    useActiveInActive(isRightDoubleTouched, isLeftDoubleTouched);

    useEffect(() => {
        const timeout = setTimeout(() => handleVideoElementSizeInfo(), 100);

        return () => clearTimeout(timeout);
    }, [showMiniPlayer]);

    useEffect(() => {
        if (conditions(ConditionName.hasSubtitles) && !conditions(ConditionName.isFirstPlay)) handleSubtitle();
    }, [firstPlay]);

    return (
        <>
            {conditions(ConditionName.showMiniPlayer) && <div id="black-video-area" className="pl-relative pl-w-full pl-aspect-video pl-bg-black" />}
            <div
                id="video-area"
                style={{
                    zIndex: conditions(ConditionName.showMiniPlayer)
                        ? zIndex.videoPlayerInPictureInPicture
                        : conditions(ConditionName.isFullScreen)
                        ? zIndex.videoPlayerInFullScreen
                        : "unset"
                }}
                onClick={(event) => conditions(ConditionName.playByClickPoster) && handlePlayPause(event)}
                onDoubleClick={(event: MouseEvent<HTMLDivElement>) => handleDoubleClick(event)}
                className={`!pl-bg-contain !pl-bg-center !pl-bg-no-repeat pl-h-full pl-bg-black pl-max-h-screen pl-aspect-video pl-flex pl-items-center ${
                    conditions(ConditionName.isFullScreen) && "!pl-fixed !pl-w-screen dvh"
                } ${
                    conditions(ConditionName.showMiniPlayer)
                        ? "!pl-fixed !pl-bottom-[10px] !pl-right-[10px] !pl-w-[400px] !pl-h-[225px]"
                        : "pl-w-full pl-relative"
                } ${!conditions(ConditionName.isComplexPortrait) && "pl-overflow-hidden"} ${
                    !conditions(ConditionName.isActiveElements) && "pl-cursor-none"
                }`}
            >
                {conditions(ConditionName.showBanner) && (
                    <div className="pl-absolute pl-w-full pl-h-full pl-flex pl-justify-center">
                        <Image src={details.banner} layout="fill" alt="sdfdf" objectFit="contain" />
                    </div>
                )}
                {conditions(ConditionName.showMiniPlayer) && (
                    <CloseIcon
                        className="pl-w-4 pl-h-4 pl-stroke-black pl-bg-white pl-absolute pl-top-1 pl-right-1 pl-z-[1] pl-cursor-pointer pl-rounded-full"
                        onClick={() => dispatch(setShowMiniPlayer(undefined))}
                    />
                )}
                {conditions(ConditionName.showTouchVolumeBrightness) && !conditions(ConditionName.isMiniPlayer) && <TouchVolumeBrightness />}
                {conditions(ConditionName.showBackwardForward) && (
                    <div
                        className={`pl-absolute pl-top-0 pl-right-0 pl-w-full pl-h-full pl-overflow-hidden pl-flex pl-items-center`}
                        style={{
                            zIndex: isLeftDoubleTouched || isRightDoubleTouched ? zIndex.backwardForwardActive : zIndex.backwardForwardInActive
                        }}
                    >
                        <BackwardForward
                            type="touchForward"
                            isRightDoubleTouched={isRightDoubleTouched}
                            setIsRightDoubleTouched={setIsRightDoubleTouched}
                        />
                        <BackwardForward
                            type="touchBackward"
                            isLeftDoubleTouched={isLeftDoubleTouched}
                            setIsLeftDoubleTouched={setIsLeftDoubleTouched}
                        />
                    </div>
                )}
                {conditions(ConditionName.isComplexPortrait) && (
                    <div
                        id="video-opacity"
                        className={`pl-absolute pl-top-0 pl-h-full pl-cursor-pointer pl-w-full pl-bg-gray-800/[0.3] animate__animated ${
                            conditions(ConditionName.isActiveElements) ? "animate__fadeIn" : "animate__fadeOut"
                        }`}
                    />
                )}
                <PlayerCore />
                {!conditions(ConditionName.isFirstPlay) && <VideoAreaShadow />}
                {conditions(ConditionName.hasContinuousPlayback) && <ContinuousPlayback />}
                {!conditions(ConditionName.isRaw) && !conditions(ConditionName.hasContinuousPlayback) && (
                    <div
                        id="playerElements"
                        className={`pl-absolute pl-top-0 pl-right-0 pl-w-full pl-h-full ${
                            conditions(ConditionName.showAutoPlayPopover) && "pl-bg-black"
                        }`}
                    >
                        <div className={`pl-flex pl-flex-col pl-justify-between pl-w-full pl-h-full`}>
                            {conditions(ConditionName.showAutoPlayPopover) && <AutoPlay />}
                            {conditions(ConditionName.showHeader) && (
                                <>{conditions(ConditionName.isComplexPortrait) ? <PortraitHeader /> : <Header />}</>
                            )}
                            {conditions(ConditionName.showMain) && !conditions(ConditionName.hasContinuousPlayback) && <Main />}
                            {conditions(ConditionName.showFooter) && <Footer />}
                            {conditions(ConditionName.showGif) && <Gif gif={gif!} />}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default VideoArea;
